import { cartSync, cartRequest, readCart, readProduct } from '@theme/fate-cart-sync';
import { giftState, isEligibleProduct, ENDS_AT, dailySecondsLeft } from './fate-mystery-gift-model.js?v=20260905-inline-1';
import { morphSection } from '@theme/section-renderer';
import { MORPH_OPTIONS } from '@theme/morph';

// Convenience guard in the storefront. Checkout bypasses require server enforcement.
const configNode = document.getElementById('fate-mystery-config');
if (configNode && !window.__fateMysteryGift) {
  window.__fateMysteryGift = true;
  const config = JSON.parse(configNode.textContent);
  const beforeUpdate = MORPH_OPTIONS.onBeforeUpdate;
  MORPH_OPTIONS.onBeforeUpdate = function(oldNode, newNode) {
    beforeUpdate?.(oldNode, newNode);
    preserveMysteryPanel(oldNode, newNode);
  };
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const eligibilityCache = new Map();
  let state, selected = '', expanded = false, saving = false;
  let timer;
  const message = 'No pudimos revisar tu regalo. Intenta de nuevo antes de continuar.';
  const block = () => document.querySelector('[data-mystery-block]');
  const variants = () => JSON.parse(block()?.querySelector('[data-mystery-variants]')?.textContent || '[]');
  async function request(path, body) {
    const response = await cartRequest(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error(message);
    return response.json();
  }
  function serial(task) {
    return cartSync.run(task);
  }

  function error(text = '') {
    const node = block()?.querySelector('[data-mystery-error]');
    if (node) { node.textContent = text; node.hidden = !text; }
  }
  async function change(path, body) {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(n => n.dataset.sectionId))].slice(0, 5);
    const result = await request(path, { ...body, sections, sections_url: location.pathname });
    const html = result.sections || {};
    await Promise.all(Object.entries(html).filter(([, value]) => value).map(([id, value]) => morphSection(id, value, 'hydration')));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
      resource: {}, sourceId: 'fate-mystery', data: { source: 'fate-mystery', rendered: true, sections: html, itemCount: result.item_count }
    } }));
    return result;
  }
  async function read() {
    const cart = await readCart();
    // A different tab may have changed the cart: rendered Liquid IDs can be stale.
    const candidates = [...new Map(cart.items.filter(item => String(item.product_id) !== String(config.productId)
      && item.original_price > 0 && !item.gift_card && !item.selling_plan_allocation).map(item => [item.product_id, item])).values()];
    const eligible = (await Promise.all(candidates.map(async item => {
      let cached = eligibilityCache.get(item.product_id);
      if (!cached || Date.now() - cached.at > 60000) {
        const product = await readProduct(item.handle);
        cached = { at: Date.now(), eligible: isEligibleProduct(product) };
        eligibilityCache.set(item.product_id, cached);
      }
      return cached.eligible ? item.product_id : null;
    }))).filter(Boolean);
    state = giftState(cart.items, eligible, config.productId, config.enabled);
    return state;
  }
  function clock() {
    const node = block()?.querySelector('[data-mystery-timer]');
    if (!node) return;
    const seconds = dailySecondsLeft(Date.now());
    node.setAttribute('data-morph-preserve', '');
    node.textContent = [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map(n => String(n).padStart(2, '0')).join(':');
  }
  function render() {
    const el = block(); if (!state || !el) return;
    el.hidden = !state.active || !config.productId;
    el.dataset.mysteryAllowed = String(state.allowed);
    el.classList.toggle('fate-mystery--claimed', Boolean(state.claimed));
    el.classList.toggle('fate-mystery--expanded', expanded);
    if (!state.allowed) { expanded = false; selected = ''; }
    el.querySelector('[data-mystery-title]').textContent = state.claimed ? 'MYSTERY TEE AGREGADA · GRATIS'
      : state.allowed ? 'MYSTERY TEE GRATIS' : 'OBTÉN UNA MYSTERY TEE GRATIS';
    el.querySelector('[data-mystery-detail]').textContent = state.allowed ? 'TÚ ELIGES LA TALLA.'
      : `AGREGA ${state.missing} PLAYERA${state.missing !== 1 ? 'S' : ''} MÁS.`;
    const choose = el.querySelector('[data-mystery-open]');
    choose.hidden = !state.allowed; choose.disabled = saving;
    choose.textContent = expanded ? 'CERRAR' : state.claimed ? 'EDITAR' : 'ELEGIR TALLA';
    choose.setAttribute('aria-expanded', String(expanded));
    el.querySelector('[data-mystery-picker]').hidden = !expanded || !state.allowed;
    el.querySelectorAll('[data-mystery-size]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.mysterySize === selected));
      button.disabled = saving || !variants().some(v => String(v.id) === button.dataset.mysterySize && v.available);
    });
    const save = el.querySelector('[data-mystery-save]');
    save.disabled = saving || !selected;
    save.textContent = saving ? 'GUARDANDO…' : state.claimed ? 'GUARDAR TALLA' : 'AGREGAR MI REGALO';
    clock();
  }
  async function reconcile() {
    await read();
    for (let attempt = 0; state.changes.length && attempt < 20; attempt++) {
      const item = state.changes[0]; await change('cart/change.js', { id: item.key, quantity: item.quantity }); await read();
    }
    if (state.changes.length) throw new Error(message);
    render(); return state;
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(() => serial(reconcile).catch(() => error(message)), 180); }
  async function saveGift(id) {
    await reconcile();
    if (!state.allowed || !variants().some(v => String(v.id) === id && v.available && v.price === 0)) throw new Error('Tu carrito cambió. Revisa la talla e intenta de nuevo.');
    const existing = state.gifts.find(item => String(item.variant_id) === id);
    for (const item of state.gifts) if (item !== existing) await change('cart/change.js', { id: item.key, quantity: 0 });
    if (!existing) await change('cart/add.js', { items: [{ id: Number(id), quantity: 1, properties: { _fate_mystery_gift: '2026-09-08' } }] });
    expanded = false; selected = ''; await reconcile();
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-mystery-open], [data-mystery-edit], [data-mystery-size], [data-mystery-save]');
    if (!button) return;
    event.preventDefault(); if (saving || button.disabled) return;
    if (button.hasAttribute('data-mystery-size')) { selected = button.dataset.mysterySize; error(); render(); return; }
    if (button.hasAttribute('data-mystery-save')) {
      if (!selected) return;
      const id = selected; saving = true; error(); render();
      serial(() => saveGift(id)).catch(err => error(err.message || message)).finally(() => { saving = false; render(); });
      return;
    }
    button.disabled = true;
    serial(reconcile).then(() => {
      if (!state.allowed) return;
      expanded = button.hasAttribute('data-mystery-edit') || !expanded;
      selected = state.gifts[0] ? String(state.gifts[0].variant_id) : '';
      error(); render();
      if (expanded) block()?.querySelector('[data-mystery-size]:not(:disabled)')?.focus({ preventScroll: false });
    }).catch(() => error(message)).finally(() => { button.disabled = false; });
  }, true);
  // The shared checkout barrier first drains quantities, adds and extras.
  cartSync.validators.add(() => { clearTimeout(timer); return serial(reconcile); });
  ['cart:update', 'cart:add', 'cart:change', 'cart:refresh'].forEach(name => document.addEventListener(name, event => { if (event.detail?.data?.source !== 'fate-mystery') schedule(); }));
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { schedule(); clock(); } });
  const remaining = ENDS_AT - Date.now();
  if (remaining > 0) setTimeout(schedule, Math.min(remaining + 50, 2147483647));
  function tickClock() {
    if (!document.hidden) clock();
    if (Date.now() < ENDS_AT) setTimeout(tickClock, 1000 - Date.now() % 1000 + 10);
  }
  tickClock();
  if (config.productId) schedule();
}

// Extras alter totals, not the gift. Keep the existing DOM, clock and selection
// when the server confirms identical eligibility, gift and variant availability.
export function preserveMysteryPanel(oldNode, newNode) {
  if (!(oldNode instanceof Element) || !(newNode instanceof Element) ||
      !oldNode.hasAttribute('data-mystery-block') || !newNode.hasAttribute('data-mystery-block')) return;
  const signature = oldNode.getAttribute('data-mystery-signature');
  if (!signature || signature !== newNode.getAttribute('data-mystery-signature')) return;
  for (const attr of [...newNode.attributes]) newNode.removeAttribute(attr.name);
  for (const attr of oldNode.attributes) newNode.setAttribute(attr.name, attr.value);
  oldNode.setAttribute('data-skip-subtree-update', '');
  newNode.setAttribute('data-skip-subtree-update', '');
}
